import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {Loader} from 'lucide-react';
import { API_URL, doApiGet } from '../services/apiService';
import { FamilyContext } from '../Context/FamilyHeadContext';
import { formatFullName } from '../utils/personUtils';
import {useTranslation} from 'react-i18next';

const ProfileChanges = ({selectedFamily}) => {
    const [changedPersons, setChangedPersons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { userDisconnection, pathPrefix } = useContext(FamilyContext);
    const {i18n} = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchChangedPersons = async () => {
            try {
                // תיקון הנתיב ל-API
                const response = await doApiGet(`${API_URL}/change/persons/${selectedFamily}`);
                setChangedPersons(Array.isArray(response) ? response : []);
                setError(null);
            } catch (error) {
                console.error('Error fetching changed persons:', error);
                setError('אירעה שגיאה בטעינת הנתונים');
                if (error.response?.status === 401 || error.response?.status === 403) {
                    userDisconnection();
                    navigate(`${pathPrefix}login`);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchChangedPersons();
    }, [selectedFamily, userDisconnection, navigate, pathPrefix]);

    const handlePersonClick = (gedId) => {
        if (gedId) {
            navigate(`${pathPrefix}person/${gedId}`);
        }
    };

    const getPersonChanges = (person) => {
        const changes = [];

        // שינוי שם משפחה
        if (person.adminFamilyName) {
            changes.push(`שינוי שם משפחה ל-${person.adminFamilyName}`);
        }

        // שינוי סדר בני זוג
        if (Array.isArray(person.adminSpouseId) && person.adminSpouseId.length > 0) {
            changes.push(`סדר בני זוג שונה (${person.adminSpouseId.length} בני זוג)`);
        }

        // שינוי סטטוס נישואין
        if (typeof person.isNotMarried === 'boolean') {
            changes.push(person.isNotMarried ? 'סטטוס: לא נשוי/אה' : 'סטטוס: נשוי/אה');
        }

        return changes;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center p-8">
                <Loader className="w-8 h-8 animate-spin text-[#937c5d]"/>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg text-center">
                {error}
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl p-6 shadow-md" dir="rtl">
            <div className="mb-6">
                <h2 className="text-xl font-bold text-[#937c5d]">
                    שינויים בפרופילים
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                    {selectedFamily === 'bazach' ? 'משפחת בזך' : 'משפחת הומינר'}
                </p>
            </div>

            {changedPersons.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
                    {changedPersons.map((person) => {
                        const changes = getPersonChanges(person);
                        if (changes.length === 0) return null;

                        return (
                            <div
                                key={person.gedId}
                                onClick={() => handlePersonClick(person.gedId)}
                                className="bg-gray-50 rounded-lg p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                            >
                                <div className="font-medium text-[#4a3f30] mb-2">
                                    {formatFullName(person, i18n.language)}
                                </div>
                                <div className="space-y-2">
                                    {changes.map((change, index) => (
                                        <div key={index} className="text-sm text-gray-600">
                                            {change}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center text-gray-500 py-8">
                    לא נמצאו שינויים בפרופילים
                </div>
            )}
        </div>
    );
};

export default ProfileChanges;